import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useDispatch } from 'src/store/useDispatch';
import { fetchSettings } from 'src/store/slice/settings';
import { setAppAlert } from 'src/store/slice/appAlert';

import PagePanel from 'src/components/PagePanel';
import PageHeader from 'src/components/PageHeader';
import { Input } from 'src/components/Input';
import { Select } from 'src/components/Select';
import { Button } from 'src/components/Button';
import { ContentSkeleton } from 'src/components/ContentSkeleton';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { PERMISSIONS } from 'src/utils/permissions';

import request from 'src/request';
import { AUTH_URL } from 'src/configs';

import {
  EmptyState,
  FieldError,
  FieldHint,
  FieldMeta,
  FieldPreview,
  FormActions,
  SettingField,
  SettingsCard,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsFields,
  FeeTable,
  FeeRow,
  FeeHead,
  FeeChain,
  FeeChainName,
  NetworkTag,
  FeeWarning,
  FeeRowActions,
} from './styled';

type SettingName = 'SPREAD' | 'MAKER_FEE' | 'TAKER_FEE';

const MIN = 0;
const MAX = 0.5;

/** Returns the field's error, or null when the value is acceptable. */
const validateSettingValue = (raw: string) => {
  if (raw.trim() === '') return 'Required';
  const value = Number(raw);
  if (!Number.isFinite(value)) return 'Must be a number';
  if (value < MIN || value > MAX) return `Must be between ${MIN} and ${MAX}`;
  return null;
};

/** One blockchain × network row of GET /v1/admin/settings/withdrawal-fees. */
type FeeRowData = {
  id: string | null;
  blockchain: { id: string; name: string; vaultody_name: string; native_currency: string | null };
  network: string;
  network_type: string;
  unit: string | null;
  slow_fee: string | null;
  standard_fee: string | null;
  fast_fee: string | null;
  currencies: string[];
  unit_options?: string[];
  unresolvable: string[];
};

type FeeDraft = { unit: string; slow_fee: string; standard_fee: string; fast_fee: string };

const feeKey = (row: FeeRowData) => `${row.blockchain.id}:${row.network}`;

/**
 * Amounts arrive as plain decimal strings; this only guards against an older API still
 * sending Decimal128's scientific notation ("1.7E-7"), which the fields cannot edit.
 */
const plainDecimal = (raw: string | null) => {
  if (raw == null) return '';
  if (!/[eE]/.test(raw)) return raw;
  const value = Number(raw);
  if (!Number.isFinite(value)) return raw;
  return value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 18 });
};

const draftFrom = (row: FeeRowData): FeeDraft => ({
  unit: row.unit ?? row.blockchain.native_currency ?? '',
  slow_fee: plainDecimal(row.slow_fee),
  standard_fee: plainDecimal(row.standard_fee),
  fast_fee: plainDecimal(row.fast_fee),
});

/** Non-negative decimal written out in full (what the API stores exactly). */
const validateFeeAmount = (raw: string) => {
  if (raw.trim() === '') return 'Required';
  if (!/^\d+(\.\d{1,18})?$/.test(raw.trim())) return 'Must be a decimal like 0.0001';
  return null;
};

const formatPercentPreview = (raw: string) => {
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  const pct = value * 100;
  const digits = pct >= 10 ? 2 : pct >= 1 ? 3 : 4;
  return `${pct.toFixed(digits).replace(/\.?0+$/, '')}%`;
};

const Settings = () => {
  const [saving, setSaving] = useState(false);
  const [spread, setSpread] = useState('');
  const [makerFee, setMakerFee] = useState('');
  const [takerFee, setTakerFee] = useState('');
  // What the server last told us, so we only send fields that actually changed.
  const [saved, setSaved] = useState<Record<SettingName, string>>({
    SPREAD: '',
    MAKER_FEE: '',
    TAKER_FEE: '',
  });

  const dispatch = useDispatch();
  const { settings, loading: fetchSettingsLoading } = useSelector(({ settings }) => settings);
  // Withdrawal fees (transaction_fee), one row per blockchain × network. Loaded here
  // rather than through the settings slice: it is a different resource with its own
  // endpoint, and nothing else on the app reads it.
  const [feeRows, setFeeRows] = useState<FeeRowData[]>([]);
  const [feeDrafts, setFeeDrafts] = useState<Record<string, FeeDraft>>({});
  const [feesLoading, setFeesLoading] = useState(true);
  const [feeSavingKey, setFeeSavingKey] = useState<string | null>(null);


  const notify = (message: string, isSuccess: boolean) =>
    dispatch(setAppAlert({ message, isSuccess }));

  const handleNumberChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      // Only the shape is enforced while typing. Range is reported on the field
      // and checked on save — silently dropping keystrokes made the input look
      // broken, since "0" + "5" exceeds the max before it can become "0.05".
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
      setter(value);
    };

  useEffect(() => {
    if (!settings.length) return;

    const findValue = (name: SettingName) => {
      const item = settings.find((s: { name: string }) => s.name === name);
      return item?.value != null ? String(item.value) : '';
    };

    const next = {
      SPREAD: findValue('SPREAD'),
      MAKER_FEE: findValue('MAKER_FEE'),
      TAKER_FEE: findValue('TAKER_FEE'),
    };

    setSpread(next.SPREAD);
    setMakerFee(next.MAKER_FEE);
    setTakerFee(next.TAKER_FEE);
    setSaved(next);
  }, [settings]);

  useEffect(() => {
    if (!settings.length) {
      dispatch(fetchSettings());
    }
  }, [dispatch, settings.length]);

  const loadFees = React.useCallback(async () => {
    setFeesLoading(true);
    try {
      const { data } = await request.get(`${AUTH_URL}/v1/admin/settings/withdrawal-fees`);
      const rows: FeeRowData[] = data?.fees ?? [];
      setFeeRows(rows);
      setFeeDrafts(Object.fromEntries(rows.map((row) => [feeKey(row), draftFrom(row)])));
    } catch (e: any) {
      notify(e?.response?.data?.error || 'Could not load withdrawal fees', false);
    } finally {
      setFeesLoading(false);
    }
    // notify is stable enough for our purpose: it only wraps dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFees();
  }, [loadFees]);

  const setFeeDraft = (key: string, patch: Partial<FeeDraft>) =>
    setFeeDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleFeeAmountChange = (key: string, field: keyof FeeDraft) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setFeeDraft(key, { [field]: value });
  };

  const feeErrors = (draft: FeeDraft) =>
    (['slow_fee', 'standard_fee', 'fast_fee'] as const).reduce<Partial<Record<keyof FeeDraft, string>>>((acc, field) => {
      const error = validateFeeAmount(draft[field]);
      if (error) acc[field] = error;
      return acc;
    }, {});

  const feeChanged = (row: FeeRowData, draft: FeeDraft) => {
    const base = draftFrom(row);
    return (Object.keys(base) as (keyof FeeDraft)[]).some((field) => base[field] !== draft[field]);
  };

  // One request per row: the API upserts by blockchain + network, so a row that never
  // had a fee is created the same way an existing one is replaced.
  const handleSaveFee = async (row: FeeRowData) => {
    const key = feeKey(row);
    const draft = feeDrafts[key];
    if (!draft || Object.keys(feeErrors(draft)).length) return;

    setFeeSavingKey(key);
    try {
      const { data } = await request.put(`${AUTH_URL}/v1/admin/settings/withdrawal-fees`, {
        blockchain_id: row.blockchain.id,
        network: row.network,
        unit: draft.unit,
        slow_fee: draft.slow_fee.trim(),
        standard_fee: draft.standard_fee.trim(),
        fast_fee: draft.fast_fee.trim(),
      });
      const saved: FeeRowData | undefined = data?.fee;
      if (saved) {
        setFeeRows((prev) => prev.map((r) => (feeKey(r) === key ? saved : r)));
        setFeeDrafts((prev) => ({ ...prev, [key]: draftFrom(saved) }));
        notify(
          saved.unresolvable.length
            ? `Fee saved. Withdrawals of ${saved.unresolvable.join(', ')} still cannot convert a ${saved.unit} fee.`
            : `${row.blockchain.name} ${row.network} fee saved`,
          !saved.unresolvable.length,
        );
      }
    } catch (e: any) {
      notify(e?.response?.data?.error || 'Fee update failed', false);
    } finally {
      setFeeSavingKey(null);
    }
  };

  const fields: {
    name: SettingName;
    label: string;
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
    hint: string;
  }[] = [
    {
      name: 'SPREAD',
      label: 'Spread',
      value: spread,
      setValue: setSpread,
      hint: 'Applied to executed price on exchange orders.',
    },
    {
      name: 'MAKER_FEE',
      label: 'Maker fee',
      value: makerFee,
      setValue: setMakerFee,
      hint: 'Fee rate for limit / maker orders.',
    },
    {
      name: 'TAKER_FEE',
      label: 'Taker fee',
      value: takerFee,
      setValue: setTakerFee,
      hint: 'Fee rate for market / taker orders.',
    },
  ];

  const errors = fields.reduce<Partial<Record<SettingName, string>>>((acc, item) => {
    const error = validateSettingValue(item.value);
    if (error) acc[item.name] = error;
    return acc;
  }, {});

  const changed = fields.filter((item) => item.value !== saved[item.name]);
  const hasErrors = Object.keys(errors).length > 0;

  // The API takes one setting per request, so "save everything" is one request
  // per changed field. Unchanged fields are skipped rather than rewritten.
  const handleSaveAll = async () => {
    if (hasErrors || changed.length === 0) return;

    setSaving(true);
    const failed: string[] = [];

    for (const item of changed) {
      try {
        await request.put(`${AUTH_URL}/v1/admin/settings`, {
          value: Number(item.value),
          name: item.name,
        });
      } catch (e: any) {
        failed.push(`${item.label}: ${e?.response?.data?.error || 'update failed'}`);
      }
    }

    setSaving(false);
    dispatch(fetchSettings());

    if (failed.length) {
      notify(failed.join('; '), false);
      return;
    }

    notify(changed.length === 1 ? 'Setting updated' : 'Settings updated', true);
  };

  const handleReset = () => {
    setSpread(saved.SPREAD);
    setMakerFee(saved.MAKER_FEE);
    setTakerFee(saved.TAKER_FEE);
  };

  return (
    <PagePanel>
      <PageHeader title="Settings" hideSearch />
      {fetchSettingsLoading ? (
        <ContentSkeleton variant="fields" count={3} />
      ) : !settings.length ? (
        <EmptyState>No application settings found.</EmptyState>
      ) : (
        <SettingsCard>
          <SettingsCardHeader>
            <SettingsCardTitle>Trading fees</SettingsCardTitle>
            <SettingsCardDescription>
              Decimal rates from {MIN} to {MAX}. Example: <code>0.001</code> is 0.1%.
            </SettingsCardDescription>
          </SettingsCardHeader>

          <SettingsFields>
            {fields.map(({ name, label, value, setValue, hint }) => {
              const preview = formatPercentPreview(value);
              const error = errors[name];

              return (
                <SettingField key={name}>
                  <Input
                    type="text"
                    label={label}
                    placeholder="0.001"
                    value={value}
                    onChange={handleNumberChange(setValue)}
                    disabled={saving}
                  />
                  <FieldMeta>
                    <FieldHint>{hint}</FieldHint>
                    {error ? (
                      <FieldError>{error}</FieldError>
                    ) : (
                      preview != null && <FieldPreview>≈ {preview}</FieldPreview>
                    )}
                  </FieldMeta>
                </SettingField>
              );
            })}
          </SettingsFields>

          <PermissionGuard permission={PERMISSIONS.SETTINGS_WRITE}>
            <FormActions>
              <Button
                text="Reset"
                className="outline"
                onClick={handleReset}
                disabled={saving || changed.length === 0}
              />
              <Button
                text="Save changes"
                className="black"
                onClick={handleSaveAll}
                isLoading={saving}
                disabled={saving || hasErrors || changed.length === 0}
              />
            </FormActions>
          </PermissionGuard>
        </SettingsCard>
      )}

      <SettingsCard>
        <SettingsCardHeader>
          <SettingsCardTitle>Withdrawal fees</SettingsCardTitle>
          <SettingsCardDescription>
            What a user pays on top of the amount, per chain, in the chosen unit. The network
            fee itself is paid by the treasury, so keep these above the real network cost.
            Only the network this environment sends on is listed.
          </SettingsCardDescription>
        </SettingsCardHeader>

        {feesLoading ? (
          <ContentSkeleton variant="fields" count={3} />
        ) : !feeRows.length ? (
          <EmptyState>No blockchains configured.</EmptyState>
        ) : (
          <FeeTable>
            <FeeHead>
              <span>Chain / network</span>
              <span>Unit</span>
              <span>Slow</span>
              <span>Standard</span>
              <span>Fast</span>
              <span />
            </FeeHead>
            {feeRows.map((row) => {
              const key = feeKey(row);
              const draft = feeDrafts[key] ?? draftFrom(row);
              const errors = feeErrors(draft);
              const changed = feeChanged(row, draft);
              const saving = feeSavingKey === key;
              // The API says what may be a unit: the chain's gas coin plus its currencies.
              const unitOptions = Array.from(
                new Set([
                  ...(row.unit_options ?? [row.blockchain.native_currency, ...row.currencies]),
                  draft.unit,
                ].filter(Boolean) as string[]),
              ).map((code) => ({ value: code, label: code }));

              return (
                <FeeRow key={key}>
                  <FeeChain>
                    <FeeChainName>{row.blockchain.name}</FeeChainName>
                    <NetworkTag>{row.network}</NetworkTag>
                    {!row.id && <FieldHint>No fee yet: withdrawals on this network fail until one is saved.</FieldHint>}
                    {row.unresolvable.length > 0 && (
                      <FeeWarning>
                        A {row.unit} fee cannot be converted for {row.unresolvable.join(', ')}; those withdrawals fail.
                      </FeeWarning>
                    )}
                  </FeeChain>
                  <Select
                    value={draft.unit}
                    options={unitOptions}
                    onChange={(e) => setFeeDraft(key, { unit: e.target.value })}
                    disabled={saving || feeSavingKey !== null}
                  />
                  {(['slow_fee', 'standard_fee', 'fast_fee'] as const).map((field) => (
                    <div key={field}>
                      <Input
                        type="text"
                        placeholder="0.0001"
                        value={draft[field]}
                        onChange={handleFeeAmountChange(key, field)}
                        disabled={saving || feeSavingKey !== null}
                      />
                      {errors[field] && <FieldError>{errors[field]}</FieldError>}
                    </div>
                  ))}
                  <FeeRowActions>
                    <PermissionGuard permission={PERMISSIONS.SETTINGS_WRITE}>
                      <Button
                        text={row.id ? 'Save' : 'Create'}
                        className="black"
                        onClick={() => handleSaveFee(row)}
                        isLoading={saving}
                        disabled={saving || feeSavingKey !== null || !changed || Object.keys(errors).length > 0}
                      />
                    </PermissionGuard>
                  </FeeRowActions>
                </FeeRow>
              );
            })}
          </FeeTable>
        )}
      </SettingsCard>
    </PagePanel>
  );
};

export default Settings;
